import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PORTS = [
  [30.32, 78.03],
  [25.2, 55.3],
  [51.9, 4.5],
  [1.35, 103.8],
  [40.7, -74.0],
];

const toXYZ = (lat, lon) => {
  const la = (lat * Math.PI) / 180;
  const lo = (lon * Math.PI) / 180;
  return [Math.cos(la) * Math.cos(lo), Math.sin(la), Math.cos(la) * Math.sin(lo)];
};

const TradeGlobe = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 750;
    const pts = [];
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const th = i * 2.399963;
      pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
    }
    const portPts = PORTS.map((p) => toXYZ(p[0], p[1]));

    const draw = () => {
      t += 0.0028;
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.4;
      ctx.clearRect(0, 0, w, h);

      const ry = t;
      const rx = 0.38 + Math.sin(t * 0.5) * 0.05;
      const proj = (v) => {
        const x1 = v[0] * Math.cos(ry) + v[2] * Math.sin(ry);
        const z1 = -v[0] * Math.sin(ry) + v[2] * Math.cos(ry);
        const y1 = v[1] * Math.cos(rx) - z1 * Math.sin(rx);
        const z2 = v[1] * Math.sin(rx) + z1 * Math.cos(rx);
        const s = 1 / (1.55 - z2 * 0.45);
        return [cx + x1 * R * s * 1.12, cy - y1 * R * s * 1.12, z2];
      };

      for (const p of pts) {
        const [px, py, pz] = proj(p);
        const a = (pz + 1) / 2;
        ctx.fillStyle = `rgba(212,175,55,${0.05 + 0.3 * a})`;
        ctx.fillRect(px - 0.8, py - 0.8, 1.6, 1.6);
      }

      for (let i = 1; i < portPts.length; i++) {
        const a = portPts[0];
        const b = portPts[i];
        ctx.beginPath();
        for (let s = 0; s <= 36; s++) {
          const k = s / 36;
          let x = a[0] + (b[0] - a[0]) * k;
          let y = a[1] + (b[1] - a[1]) * k;
          let z = a[2] + (b[2] - a[2]) * k;
          const len = Math.hypot(x, y, z) || 1;
          const bl = 1 + 0.4 * Math.sin(k * Math.PI);
          const [px, py] = proj([(x / len) * bl, (y / len) * bl, (z / len) * bl]);
          if (s === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = "rgba(125,211,252,0.4)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      portPts.forEach((p, i) => {
        const [px, py, pz] = proj(p);
        if (pz > -0.2) {
          const pulse = (Math.sin(t * 14 + i * 2) + 1) / 2;
          ctx.fillStyle = i === 0 ? "#EE9B00" : "#7DD3FC";
          ctx.beginPath();
          ctx.arc(px, py, 2 + pulse * 1.6, 0, 7);
          ctx.fill();
        }
      });

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full h-full" aria-hidden="true" />;
};

const Hero = ({ ready }) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const globeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen bg-forest grain overflow-hidden flex flex-col"
    >
      {/* ambient glows */}
      <div
        className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #7DD3FC 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-52 -left-40 w-[40rem] h-[40rem] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #E09F3E 0%, transparent 70%)" }}
      />

      {/* ambient trade globe */}
      <motion.div
        style={{ y: globeY }}
        className="absolute right-[-18%] sm:right-[-8%] lg:right-[2%] top-1/2 -translate-y-1/2 w-[34rem] h-[34rem] lg:w-[46rem] lg:h-[46rem] opacity-70 pointer-events-none"
      >
        <TradeGlobe />
      </motion.div>

      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-12 lg:px-20 pt-32 pb-16">
        <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          {/* headline block */}
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={ready ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-sunset uppercase mb-6"
            >
              Nesha Global Pvt. Ltd. — Est. 2026 · Dehradun, India
            </motion.p>

            <h1 className="font-display uppercase text-cream leading-[1.02] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight">
              {["From Indian Soil", "To The World's", "Table."].map((l, i) => (
                <span key={l} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    custom={i}
                    variants={line}
                    initial="hidden"
                    animate={ready ? "show" : "hidden"}
                  >
                    {i === 2 ? (
                      <>
                        <span className="text-sunset italic normal-case">Table.</span>
                      </>
                    ) : (
                      l
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={ready ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-7 max-w-xl text-cream/70 text-sm sm:text-base leading-relaxed"
            >
              Premium global food sourcing &amp; logistics — every agricultural product
              we import and export meets the highest standards of quality and
              sustainability, with integrity and precision.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={ready ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-products"
                onClick={() => scrollToSection("#products")}
                className="group rounded-full bg-sunset px-8 py-4 font-grotesk text-sm font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300"
              >
                Explore Products
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform duration-300">
                  →
                </span>
              </button>
              <button
                data-testid="hero-cta-contact"
                onClick={() => scrollToSection("#contact")}
                className="rounded-full border border-cream/30 px-8 py-4 font-grotesk text-sm font-medium uppercase tracking-wider text-cream hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Contact Trade Desk
              </button>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={ready ? { opacity: 1 } : {}}
              transition={{ duration: 1, delay: 1.15 }}
              className="mt-12 flex flex-wrap gap-x-6 gap-y-3 font-mono text-[10px] sm:text-xs tracking-[0.2em] text-cream/50 uppercase"
            >
              {["07 Product Lines", "MCA SPICE+ Certified", "Sea & Air Freight", "Dehradun HQ"].map(
                (f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                    {f}
                  </li>
                )
              )}
            </motion.ul>
          </motion.div>

          {/* arch image + rotating badge */}
          <div className="lg:col-span-5 relative hidden sm:block">
            <motion.div
              style={{ y: imgY }}
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={ready ? { clipPath: "inset(0% 0% 0% 0%)" } : {}}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
              className="relative mx-auto w-72 lg:w-80 xl:w-96 aspect-[3/4] rounded-t-full rounded-b-[2rem] overflow-hidden ring-1 ring-gold/40 shadow-2xl"
            >
              <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-[3px] bg-forest/70">
                {[
                  {
                    id: "rice",
                    src: "https://images.unsplash.com/photo-1536304993881-ff6e9eefa2a6?q=80&w=800&auto=format&fit=crop",
                    alt: "Premium basmati rice grains",
                  },
                  {
                    id: "onion",
                    src: "https://images.unsplash.com/photo-1618512496248-a07fe83aa8cb?q=80&w=800&auto=format&fit=crop",
                    alt: "Fresh red onions for export",
                  },
                  {
                    id: "spices",
                    src: "https://images.unsplash.com/photo-1532336414038-cf19250c5757?q=80&w=800&auto=format&fit=crop",
                    alt: "Colourful Indian spices",
                  },
                  {
                    id: "fruits",
                    src: "https://images.unsplash.com/photo-1669207334420-66d0e3450283?q=80&w=800&auto=format&fit=crop",
                    alt: "Fresh mangoes and tropical fruits",
                  },
                ].map((tile) => (
                  <div key={tile.id} className="overflow-hidden">
                    <img
                      data-testid={`hero-arch-image-${tile.id}`}
                      src={tile.src}
                      alt={tile.alt}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                ))}
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={ready ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.7, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-8 -left-6 lg:-left-12 w-28 h-28 lg:w-32 lg:h-32"
            >
              <div className="relative w-full h-full rounded-full bg-cream shadow-xl ring-1 ring-gold/40 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-spin-slow">
                  <defs>
                    <path
                      id="badge-circle"
                      d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0"
                    />
                  </defs>
                  <text className="font-mono" fontSize="8.2" fill="#0F2E1B" letterSpacing="2.2">
                    <textPath href="#badge-circle">
                      WHERE VISION MEETS THE WORLD •
                    </textPath>
                  </text>
                </svg>
                <span className="w-12 h-12 rounded-full overflow-hidden bg-white ring-1 ring-gold/50">
                  <img
                    src="/assets/nesha-logo.png"
                    alt="Nesha Global emblem"
                    className="w-full h-full object-cover"
                  />
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* scroll hint */}
      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToSection("#about")}
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="relative z-10 mx-auto mb-6 flex flex-col items-center gap-2 text-cream/50 hover:text-gold transition-colors duration-300"
        aria-label="Scroll to about section"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-cream/30 overflow-hidden">
          <span className="block w-full h-full bg-gold animate-scroll-hint" />
        </span>
        <ArrowDown className="w-3 h-3" />
      </motion.button>
    </section>
  );
};

export default Hero;
